/**
 * Algolia analytics for click tracking and conversions.
 *
 * Uses the Algolia Insights API to track:
 * - Product clicks from search results
 * - Product conversions (view PDP)
 * - Add to cart events
 * - Purchase events
 *
 * These events help improve search relevance through Algolia's AI features.
 */

'use client';

import { devWarn } from '@/lib/utils/devLog';
import { ALGOLIA_CONFIG } from './config';
import type { AlgoliaEventType, ClickEventData, ConversionEventData } from './types';

// User token for anonymous users (persisted in localStorage)
const USER_TOKEN_KEY = 'algolia_user_token';

/**
 * Get or create a user token for Algolia analytics.
 *
 * Anonymous users get a generated token persisted in localStorage.
 * Authenticated users should use their actual user ID.
 */
export function getAlgoliaUserToken(): string {
  if (typeof window === 'undefined') {
    return 'anonymous-ssr';
  }

  let token = localStorage.getItem(USER_TOKEN_KEY);

  if (!token) {
    // Generate a random token for anonymous users
    token = `anon-${crypto.randomUUID()}`;
    localStorage.setItem(USER_TOKEN_KEY, token);
  }

  return token;
}

/**
 * Set the user token (e.g., when user logs in).
 */
export function setAlgoliaUserToken(userId: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(USER_TOKEN_KEY, userId);
  }
}

/**
 * Send an event to Algolia Insights API.
 */
async function sendInsightsEvent(
  eventType: AlgoliaEventType,
  eventName: string,
  data: ClickEventData | ConversionEventData
): Promise<void> {
  const userToken = getAlgoliaUserToken();

  const payload = {
    events: [
      {
        eventType,
        eventName,
        index: data.index,
        userToken,
        objectIDs: data.objectIDs,
        ...('queryID' in data && data.queryID ? { queryID: data.queryID } : {}),
        ...('positions' in data && data.positions ? { positions: data.positions } : {}),
        ...('value' in data && data.value !== undefined ? { value: data.value } : {}),
        ...('currency' in data && data.currency ? { currency: data.currency } : {}),
        timestamp: Date.now(),
      },
    ],
  };

  try {
    await fetch(`https://insights.algolia.io/1/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Algolia-Application-Id': ALGOLIA_CONFIG.appId,
        'X-Algolia-API-Key': ALGOLIA_CONFIG.apiKey,
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // Silently fail analytics - don't break the user experience
    devWarn('Failed to send Algolia analytics event:', error);
  }
}

// =============================================================================
// Event Tracking Functions
// =============================================================================

/**
 * Track a product click from search results.
 *
 * Call this when a user clicks on a product from the PLP.
 *
 * @param index - Index name
 * @param objectID - Product SKU that was clicked
 * @param position - Position in search results (1-indexed)
 * @param queryID - Query ID from the search response
 */
export function trackProductClick(
  index: string,
  objectID: string,
  position: number,
  queryID?: string
): void {
  const eventType = queryID ? 'clickedObjectIDsAfterSearch' : 'clickedObjectIDs';
  const eventName = 'Product Clicked';

  sendInsightsEvent(eventType as AlgoliaEventType, eventName, {
    index,
    objectIDs: [objectID],
    queryID,
    positions: [position],
  });
}

/**
 * Track a product conversion (e.g., viewing PDP).
 *
 * Call this when a user views a product detail page.
 *
 * @param index - Index name
 * @param objectID - Product SKU
 * @param queryID - Query ID from the search that led here (if any)
 */
export function trackProductConversion(index: string, objectID: string, queryID?: string): void {
  const eventType = queryID ? 'convertedObjectIDsAfterSearch' : 'convertedObjectIDs';
  const eventName = 'Product Viewed';

  sendInsightsEvent(eventType as AlgoliaEventType, eventName, {
    index,
    objectIDs: [objectID],
    queryID,
  });
}

/**
 * Track add to cart event.
 *
 * Call this when a user adds a product to their cart.
 *
 * @param index - Index name
 * @param objectID - Product SKU
 * @param price - Product price
 * @param currency - Currency code (e.g., 'USD')
 * @param queryID - Query ID from the search that led here (if any)
 */
export function trackAddToCart(
  index: string,
  objectID: string,
  price: number,
  currency: string,
  queryID?: string
): void {
  const eventType = queryID ? 'addedToCartObjectIDsAfterSearch' : 'addedToCartObjectIDs';
  const eventName = 'Product Added to Cart';

  sendInsightsEvent(eventType as AlgoliaEventType, eventName, {
    index,
    objectIDs: [objectID],
    queryID,
    value: price,
    currency,
  });
}

/**
 * Track purchase event.
 *
 * Call this when a user completes a purchase.
 *
 * @param index - Index name
 * @param objectIDs - Array of product SKUs purchased
 * @param totalValue - Total order value
 * @param currency - Currency code
 * @param queryID - Query ID from the search that led here (if any)
 */
export function trackPurchase(
  index: string,
  objectIDs: string[],
  totalValue: number,
  currency: string,
  queryID?: string
): void {
  const eventType = queryID ? 'purchasedObjectIDsAfterSearch' : 'purchasedObjectIDs';
  const eventName = 'Products Purchased';

  sendInsightsEvent(eventType as AlgoliaEventType, eventName, {
    index,
    objectIDs,
    queryID,
    value: totalValue,
    currency,
  });
}

// =============================================================================
// React Hook for Analytics Context
// =============================================================================

/**
 * Analytics context for passing queryID through the app.
 *
 * Store the queryID from search results to associate subsequent
 * actions (clicks, conversions) with the search.
 */
export interface SearchAnalyticsContext {
  /** Index name for the search */
  indexName: string;

  /** Query ID from the search response */
  queryID?: string;
}

/**
 * Create analytics context from search result.
 *
 * Pass this through to product cards so they can track clicks correctly.
 */
export function createAnalyticsContext(
  indexName: string,
  queryID?: string
): SearchAnalyticsContext {
  return { indexName, queryID };
}
